import { RootState, store } from '../../app/store';
import { healthWalletApi, OnboardingStatus } from './healthWalletSlice';
import { createSelector } from '@reduxjs/toolkit';
import type { VerificationStatus, ClearStatus } from '../../types/onboarding';
//        const verificationStatus =
//          clearVerified && (browserTrustValid || requestState.clearVerifiedInFlow === 'success') ? VERIFICATION_STATUS.VERIFIED : !!patientByProfileIdRet.clear_user_id ? VERIFICATION_STATUS.REVERIFICATION_REQUIRED : VERIFICATION_STATUS.NEVER_VERIFIED;
// //          clearVerified && (browserTrustValid || requestState.clearVerifiedInFlow === 'success') ? VERIFICATION_STATUS.VERIFIED : !!patientByProfileIdRet.clear_user_id ? VERIFICATION_STATUS.REVERIFICATION_REQUIRED : VERIFICATION_STATUS.NEVER_VERIFIED;

// export function computeVerificationStatus(
//   clearStatus: ClearStatus,
//   browserTrustValid: boolean,
//   identityVerifiedInFlow: boolean,
//   clear_user_id_exists: boolean
// ): VerificationStatus {
//   const clearVerified = clearStatus === 'success';
//   if (clearVerified && (browserTrustValid || identityVerifiedInFlow)) return 'verified';
//   if (clear_user_id_exists) return 'reverification_required';
//   return 'never_verified';
// }



export type OnboardingStep = 'logo' | 'landing' | 'consent' | 'verification' | 're-verification' | 'trust' | 'connect' | 'data' | 'waiting';


// More efficient memoized selector
export const selectOnboardingMeta = createSelector(
  [(state: RootState) => state],
  (state) => {
    const result = healthWalletApi.endpoints.getOnboardingStatus.select()(state);
    return {
      data: result?.data,
      isLoading: result?.isLoading,
      isSuccess: result?.isSuccess,
      isError: result?.isError,
      error: result?.error
    };
  }
);

export const getOnboardingStatusWithMeta = () => {
  const meta = selectOnboardingMeta(store.getState());
  return meta;
};

/**
 * Checks if the onboarding state is currently loading
 * @returns True if onboarding status is not yet available
 */
export const isOnboardingLoading = (): boolean => {
  const meta = selectOnboardingMeta(store.getState());
  return meta.isLoading || (!meta.isSuccess && !meta.data);
};

/**
 * Determines the current onboarding step based on the RTK state
 * @returns The current onboarding step
 */
export const getCurrentOnboardingStep = (): OnboardingStep => {
  // Access onboarding status directly from store state
  // const selectOnboardingStatus = healthWalletApi.endpoints.getOnboardingStatus.select();
  // const onboardingStatus = selectOnboardingStatus(store.getState())?.data;
  const meta = getOnboardingStatusWithMeta()
  return getCurrentOnboardingStepFromMeta(meta)
}

export const passed = (dateStr: string, x: number): boolean => {
  const time = new Date(dateStr).getTime();
  if (isNaN(time)) return false;
  const days = 86400000
  const seconds = 1000
  const diffSec = (Date.now() - time) / seconds;
  console.log({
    dateStr,
    now: new Date().toISOString(),
    input: new Date(dateStr).toISOString(),
    diffSec,
  });
  return Math.floor((Date.now() - time) / seconds) >= x;
};

export const getCurrentOnboardingStepFromMeta = (meta: any): OnboardingStep => {
  const onboardingStatus: OnboardingStatus = meta.data;
  const { isLoading, isError } = meta;
  if (!onboardingStatus || isLoading || isError) {
    return 'logo';
  }

  const { consentSigned: hasAllConsents, verificationStatus, isOnboarded, browserTrusted: isTrusted, browserTrustConfirmed, clearVerificationAcknowledged, identityVerifiedAt, clearVerifiedInFlow, dataRequestStatus } = onboardingStatus;

  const trustStepComplete = isTrusted || browserTrustConfirmed;
  // clearVerificationAcknowledged is client-only state that resets on refresh. To survive
  // an interrupted/refreshed flow, treat the user as having acknowledged when the backend
  // already has identityVerifiedAt AND we're not mid-flow (clearVerifiedInFlow is null).
  // During a fresh CLEAR completion, clearVerifiedInFlow='success' keeps the user on the
  // acknowledgment screen until they click Next.
  const hasAcknowledged =
    clearVerificationAcknowledged ||
    (verificationStatus === 'verified' && !!identityVerifiedAt && !clearVerifiedInFlow);

  // verificationStatus (never_verified | reverification_required | verified) is computed by the backend
  // and can be 'verified' via browser trust alone — an isOnboarded user can arrive with
  // verificationStatus='verified' without having gone through CLEAR in any session (trusted browser
  // keeps it verified until expiry). hasAcknowledged is the persistent gate that ensures
  // every onboarded user explicitly completes the 're-verification' screen (CLEAR + acknowledge) at
  // least once before reaching 'data', regardless of how verificationStatus was satisfied.
  // ⚠️ A TRUSTED browser skips the re-verification screen ONLY while no CLEAR outcome is pending.
  // With one pending (`clearVerifiedInFlow` set) it must pass through 're-verification', whose
  // success screen is the ONLY thing that acknowledges the outcome. Otherwise this returns 'data'
  // while HealthWalletMain keeps OnboardingFlow mounted (it holds while clearVerifiedInFlow is set),
  // OnboardingFlow has no 'data' screen → a BLANK page, and the marker never clears, so every
  // reload repeats it. Hit in prod 2026-10-09 (trusted browser + expired CLEAR session → re-CLEAR).
  if (isOnboarded) {
    if (verificationStatus === 'verified' && (hasAcknowledged || (isTrusted && !clearVerifiedInFlow))) {
      return 'data';
    } else if (verificationStatus === 'verified' || verificationStatus === 'reverification_required') {
      return 're-verification';
    }
  }

  if (trustStepComplete && hasAcknowledged && verificationStatus === 'verified' && hasAllConsents) {
    // Post-trust routing keyed on the CURRENT data-request status:
    //  • retrieval started (pending/processing/completed/failed) → 'waiting' (WalletLoadingScreen shows
    //    progress/outcome + Continue; it no longer kicks the sync itself).
    //  • 'patient_discovery_completed' (discovery ran, awaiting connect+trigger) OR none/null (no discovery
    //    yet) → 'connect' / "Find my providers" (the screen runs discovery on entry if none, then surfaces
    //    the providers + "Get my records"). See project_hw_unified_data_retrieval.
    if (
      dataRequestStatus === 'pending' ||
      dataRequestStatus === 'processing' ||
      dataRequestStatus === 'completed' ||
      dataRequestStatus === 'failed'
    ) {
      return 'waiting';
    }
    return 'connect';
  } else if (hasAcknowledged && verificationStatus === 'verified' && hasAllConsents) {
    return 'trust';
  } else if (hasAllConsents) {
    return 'verification';
  } else {
    return 'landing';
  }

};


/**
 * Gets the current onboarding status object
 * @returns The onboarding status data or null if not available
 */
export const getOnboardingStatus = () => {
  const selectOnboardingStatus = healthWalletApi.endpoints.getOnboardingStatus.select();
  return selectOnboardingStatus(store.getState())?.data || null;
};
